import { useCallback, useEffect, useRef, useState } from "react";
import { useAppContext } from "../../../AppContext";
import { useApiQuery } from "../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { IInventoryProductSearchResult } from "../models/cashdeskModels";

interface IUseCashdeskProductSearchProps {
  onAutoAdd: (product: IInventoryProductSearchResult) => void;
}

export const useCashdeskProductSearch = ({
  onAutoAdd,
}: IUseCashdeskProductSearchProps) => {
  const { selectedStudioId } = useAppContext();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  const { data: searchResults } = useApiQuery<IInventoryProductSearchResult[]>({
    queryKey: [
      endpointsMetadata.inventoryItemNomenclature.searchProducts,
      debouncedQuery,
      selectedStudioId,
    ],
    endpoint: `${endpointsMetadata.inventoryItemNomenclature.searchProducts}?query=${encodeURIComponent(debouncedQuery)}`,
    enabled: debouncedQuery.length >= 1,
  });

  // Auto-add on exact barcode match (single result).
  const prevResultRef = useRef<IInventoryProductSearchResult[] | null>(null);

  useEffect(() => {
    if (
      searchResults &&
      searchResults.length === 1 &&
      debouncedQuery.length > 0 &&
      searchResults !== prevResultRef.current
    ) {
      const product = searchResults[0];
      // Check if it's an exact barcode match.
      if (
        product.barcode &&
        product.barcode.toLowerCase() === debouncedQuery.toLowerCase()
      ) {
        onAutoAdd(product);
        setSearchQuery("");
        setDebouncedQuery("");
      }
    }
    prevResultRef.current = searchResults ?? null;
  }, [searchResults, debouncedQuery, onAutoAdd]);

  const handleBarcodeScanned = useCallback(
    (value: string) => {
      setSearchQuery(value);
      setDebouncedQuery(value);
    },
    [],
  );

  return {
    searchQuery,
    setSearchQuery,
    searchResults: searchResults ?? [],
    handleBarcodeScanned,
  };
};
